import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './modal.css';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  /** Fixierte Fusszeile (scrollt nicht mit dem Inhalt — z. B. Speichern). */
  footer?: ReactNode;
}

/** Modal-Overlay; schliesst per Escape oder Klick auf den Hintergrund. */
export function Modal({ title, onClose, children, wide = false, footer }: ModalProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Per Portal an document.body: sonst wuerde ein transformierter Vorfahre
  // (z. B. die HUD-Leiste mit translateX) das position:fixed-Overlay an sich
  // binden statt an den Viewport — Zentrierung und max-height greifen dann nicht.
  return createPortal(
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`modal ${wide ? 'modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
